import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useVenuePageContext } from "Venues/contexts";
import {
  venueInfoBlockItemsSelector,
  dataMetaSelector,
  dispatchVenueInfoBlockItemsAction,
} from "Venues/state";
import { VENUE_ACTIONS } from "Venues/types";
import {
  getInfoBlockItemKey,
  useClearActionMetaOnUnMount,
  usePagination,
} from "Venues/utils";
import { ChooseInfoBlockItemModal as Component } from "shared/components";
import {
  ChooseInfoBlockItemBaseProps,
  useChooseInfoBlockItem,
} from "shared/utils";
import { IAPIState } from "shared/types";

export const ChooseInfoBlockItemModal: React.FC<ChooseInfoBlockItemBaseProps> =
  ({ type, handleClose, handleAddNew, infoBlockApiStatus, ...rest }) => {
    const { venueId } = useVenuePageContext();
    const dispatch = useDispatch();
    const { api: apiMeta } = useSelector(
      dataMetaSelector(VENUE_ACTIONS.getVenueInfoBlockItems)
    );
    const paginationKey = useMemo(
      () => getInfoBlockItemKey(venueId, type),
      [venueId, type]
    );
    const pagination = usePagination(paginationKey);
    const itemsSelector = useMemo(
      () => venueInfoBlockItemsSelector(type, pagination),
      [type, pagination]
    );
    const items = useSelector(itemsSelector);

    const fetchItems = useCallback(
      (page?: number) => {
        dispatchVenueInfoBlockItemsAction(dispatch, { venueId, type, page });
      },
      [dispatch, type, venueId]
    );

    useEffect(() => {
      fetchItems();
    }, [fetchItems]);

    useClearActionMetaOnUnMount(VENUE_ACTIONS.getVenueInfoBlockItems);

    const { fetchedOnce, openCreateModal, itemReadbleName } =
      useChooseInfoBlockItem({
        type,
        items,
        handleClose,
        handleAddNew,
        getItemsApiStatus: apiMeta?.status,
        infoBlockApiStatus,
      });

    if (apiMeta?.status === IAPIState.pending && !fetchedOnce.current)
      return null;

    return (
      <Component
        type={type}
        items={items}
        getItemsApiStatus={apiMeta?.status}
        pagination={pagination}
        fetchItems={fetchItems}
        openCreateModal={openCreateModal}
        itemReadbleName={itemReadbleName}
        handleClose={handleClose}
        {...rest}
      />
    );
  };
